import { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { horseAPI, breedsAPI, publicAPI, coatsAPI } from '@/services/api';
import { getTranslatedName } from '@/utils/i18nHelpers';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { handleSubmit as submit } from "@/utils/submitUtils";
import CostSummary from "@/components/CostSummary";
import Section from "@/components/Section";
import ImageUpload from "@/components/ImageUpload";
import VideoUpload from "@/components/VideoUpload";
import FeatureAdToggle from "@/components/FeatureAdToggle";
import LocationEditor from "@/components/LocationEditor";
import SubmitButton from "@/components/SubmitButton";
import BackLink from "@/components/BackLink";
import WaiverCheckBox from "@/components/WaiverCheckBox";
import { computeCosts, defaultPricing } from "@/utils/costUtil";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const OTHER = '__other__';

export default function CreateHorseAdPage() {
	const { t, i18n } = useTranslation();
	const { user } = useAuth();
	const navigate = useNavigate();
	const location = useLocation();
	const isClone = location.pathname.includes('clone');
	const { id: editId } = useParams();
	const [breeds, setBreeds] = useState([]);
	const [coats, setCoats] = useState([]);
	const [loading, setLoading] = useState(false);
	const [customBreed, setCustomBreed] = useState('');
	const [customCoat, setCustomCoat] = useState('');
	const [pricing, setPricing] = useState(defaultPricing());
	const [waivedWithdrawalRight, setWaivedWithdrawalRight] = useState(false);

	// Media state: each item has { url?, file?, preview, enabled, isExisting }
	const [images, setImages] = useState([]);
	const [videos, setVideos] = useState([]);

	const [form, setForm] = useState({
		title: '',
		description: '',
		date_of_birth: '',
		height: '',
		weight: '',
		price: '',
		gender: 'mare',
		color: '',
		customCoat: '',
		breed: '',
		customBreed: '',
		travel_documents: false,
		stud_book_link: '',
		health_issues: '',
		category: 'sale',
		microchip_id: '',
		location_lat: null,
		location_lng: null,
		location_name: '',
		contact_method: 'message',
		is_featured: false,
		featured_paid: false,
		hidden_images: [],
		hidden_videos: []
	});

	useEffect(() => {
		breedsAPI.list()
			.then(r => setBreeds(r.data || []))
			.catch(() => {});
		coatsAPI.list()
			.then(r => setCoats(r.data || []))
			.catch(() => {});
		publicAPI.settings()
			.then(r => setPricing(prev => ({ ...prev, ...r.data })))
			.catch(() => {});
		if (editId) {
			horseAPI.get(editId).then(r => {
				const ad = r.data;
				setForm({
					title: ad.title || '',
					description: ad.description || '',
					date_of_birth: ad.date_of_birth || '',
					height: ad.height || '',
					weight: ad.weight || '',
					price: ad.price || '',
					gender: ad.gender || 'mare',
					color: ad.color || '',
					customCoat: ad.customColor || '',
					breed: ad.breed || '',
					customBreed: ad.customBreed || '',
					travel_documents: ad.travel_documents || false,
					stud_book_link: ad.stud_book_link || '',
					health_issues: ad.health_issues || '',
					category: ad.category || 'sale',
					microchip_id: ad.microchip_id || '',
					location_lat: ad.location_lat || '',
					location_lng: ad.location_lng || '',
					location_name: ad.location_name || '',
					contact_method: ad.contact_method || 'message',
					is_featured: ad.is_featured || false,
					featured_paid: ad.featured_paid || false,
					hidden_images: ad.hidden_images || [],
					hidden_videos: ad.hidden_videos || [],
					included_images: ad.included_images || pricing.free_images_limit || 5,
					included_videos: ad.included_videos || pricing.free_videos_limit || 1,
				});
				setCustomBreed(ad.customBreed || '');
				setCustomCoat(ad.customColor || '');
				// Load existing images as thumbnails
				const existingImages = (ad.images || []).map((url, i) => ({
					url,
					preview: url.startsWith('/') ? `${ API_BASE }${ url }` : url,
					thumbnail: ad.thumbnails?.[i] ? (ad.thumbnails[i].startsWith('/') ? `${ API_BASE }${ ad.thumbnails[i] }` : ad.thumbnails[i]) : null,
					enabled: !(ad.hidden_images || []).includes(url),
					isExisting: true
				}));
				// need to remove images in case of cloning as I don't have the objects to upload
				setImages(isClone ? [] : existingImages);
				// Load existing videos
				const existingVideos = (ad.videos || []).map(url => ({
					url,
					preview: url.startsWith('/') ? `${ API_BASE }${ url }` : url,
					enabled: !(ad.hidden_videos || []).includes(url),
					isExisting: true,
					name: url.split('/').pop()
				}));
				// need to remove videos in case of cloning as I don't have the objects to upload
				setVideos(isClone ? [] : existingVideos);
			}).catch(() => navigate('/dashboard'));
		}
	}, [user, editId, navigate]);

  const update = (key, value) => {
		setForm(prev => ({ ...prev, [key]: value }));
	};

	const handleBreedChange = (v) => {
		const el = document.querySelector('[name="horse-breed-select"]');

		if (el.children.length < 2) return;

		if (v === OTHER) {
			update('breed', OTHER);
		} else {
			update('breed', v);
			setCustomBreed('');
		}
	};

	const handleColorChange = (v) => {
		const el = document.querySelector('[name="horse-color-select"]');

		if (el.children.length < 2) return;

		if (v === OTHER) {
			update('color', OTHER)
		} else {
			update('color', v)
			setCustomCoat('');
		}
	};

  // --- Image handlers ---
  const handleImages = (e) => {
    const files = Array.from(e.target.files);
    const newImgs = files.map(f => ({ file: f, preview: URL.createObjectURL(f), enabled: true, isExisting: false }));
    setImages(prev => [...prev, ...newImgs]);
  };
  const removeImage = (i) => {
    if (!images[i].isExisting) URL.revokeObjectURL(images[i].preview);
    setImages(prev => prev.filter((_, idx) => idx !== i));
  };
  const toggleImage = (i) =>
		setImages(prev => prev.map((img, idx) => idx === i ?
			{ ...img, enabled: !img.enabled } : img));

  // --- Video handlers ---
  const handleVideos = (e) => {
    const files = Array.from(e.target.files);
    const maxSize = (pricing.max_video_size_mb || 50) * 1024 * 1024;
    for (const f of files) {
      if (f.size > maxSize) {
				toast.error(t('horse.video_too_large', { size: pricing.max_video_size_mb }));
				return;
			}
    }
    const newVids = files.map(f =>
			({ file: f, preview: URL.createObjectURL(f), enabled: true, isExisting: false, name: f.name })
		);
    setVideos(prev => [...prev, ...newVids]);
  };
  const removeVideo = (i) => {
    if (!videos[i].isExisting) URL.revokeObjectURL(videos[i].preview);
    setVideos(prev => prev.filter((_, idx) => idx !== i));
  };
  const toggleVideo = (i) => setVideos(prev =>
		prev.map((v, idx) => idx === i ? { ...v, enabled: !v.enabled } : v)
	);

  // --- Submit ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.description || !form.price || !form.location_name) {
			toast.error(t('horse.required_fields_error')); return;
		}

		const cost = computeCosts(images, videos, pricing, form.is_featured, form.featured_paid, form.included_images,
			form.included_videos);

		// if total cost > 0 and waiver not ticked raise error
		if (cost.total > 0 && !waivedWithdrawalRight) {
			toast.error(t('legal.waived_withdrawal_required')); return;
		}

		const newImages = cost.enabledImages.filter(i => !i.isExisting);
		const newVideos = cost.enabledVideos.filter(v => !v.isExisting);

		// Compute hidden lists from existing media
		const hiddenImgs = images.filter(i => i.isExisting && !i.enabled).map(i => i.url);
		const hiddenVids = videos.filter(v => v.isExisting && !v.enabled).map(v => v.url);

		const payload = {
			...form,
			breed: form.breed,
			customBreed: customBreed,
			color: form.color,
			customColor: customCoat,
			price: parseFloat(form.price),
			height: form.height ? parseFloat(form.height) : null,
			weight: form.weight ? parseFloat(form.weight) : null,
			location_lat: form.location_lat ? parseFloat(form.location_lat) : null,
			location_lng: form.location_lng ? parseFloat(form.location_lng) : null,
			included_images: Math.max(pricing.free_images_limit, cost.enabledImages.length),
			included_videos: Math.max(pricing.free_videos_limit, cost.enabledVideos.length),
			hidden_images: hiddenImgs,
			hidden_videos: hiddenVids,
		};

		await submit(payload, horseAPI, isClone ? null : editId, 'horse', cost,
			newImages, newVideos,	form.is_featured, form.featured_paid, setLoading, t, navigate);

  };

  return (
    <div className="pt-20 md:pt-24 min-h-screen" data-testid="create-horse-ad-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

				<BackLink title={form.title} uri="/horses" textKey='nav.back_to_results' />

        <h1 className="text-3xl font-medium text-[#1A3C34] font-['Playfair_Display'] mb-8">
          { isClone ? t('horse.create_ad') : editId ? t('horse.edit_ad') : t('horse.create_ad')}
        </h1>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Info */}
          <Section title={t('horse.basic_info')}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label className="text-xs font-semibold text-[#666055] uppercase tracking-wider">
									{t('horse.title')} *
								</Label>
								<Input data-testid="horse-title" required value={ form.title }
											 onChange={ e => update('title', e.target.value) }
											 className="mt-1 bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-11"
											 placeholder={ t('horse.title_placeholder') }/>
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs font-semibold text-[#666055] uppercase tracking-wider">
									{t('horse.description')} *
								</Label>
								<Textarea data-testid="horse-description" required value={ form.description }
													onChange={ e => update('description', e.target.value) }
													className="mt-1 bg-[#F9F7F2] border-[#E6E2D6] rounded-sm min-h-[120px]"/>
              </div>
              <div>
                <Label className="text-xs font-semibold text-[#666055] uppercase tracking-wider">
									{t('horse.price')} (EUR) *
								</Label>
								<Input data-testid="horse-price" type="number" required value={ form.price }
											 onChange={ e => update('price', e.target.value) }
											 className="mt-1 bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-11"/>
              </div>
              <div>
                <Label className="text-xs font-semibold text-[#666055] uppercase tracking-wider">
									{t('horse.category')}
								</Label>
                <Select value={form.category} onValueChange={v => update('category', v)}>
                  <SelectTrigger data-testid="horse-category"
																 className="mt-1 bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-11">
										<SelectValue />
									</SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sale">{t('horse.sale')}</SelectItem>
                    <SelectItem value="stallion">{t('horse.stallion')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </Section>

          {/* Horse Details */}
          <Section title={t('horse.horse_details')}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-semibold text-[#666055] uppercase tracking-wider">
									{t('horse.breed')}
								</Label>
								<Select name="horse-breed-select" value={ form.breed } onValueChange={ handleBreedChange }>
									<SelectTrigger data-testid="horse-breed"
																 className="mt-1 bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-11">
										<SelectValue placeholder={ t('horse.select_breed') }/>
									</SelectTrigger>
									<SelectContent>
										{ breeds.map(b =>
											<SelectItem key={ b.id } value={ b.name }>
												{ getTranslatedName(b, i18n.language) }
											</SelectItem>
										) }
										<SelectItem value={ OTHER }>{ t('horse.other') }</SelectItem>
									</SelectContent>
								</Select>
                {form.breed === OTHER && (
									<Input data-testid="horse-custom-breed" value={ customBreed }
												 onChange={ e => setCustomBreed(e.target.value) }
												 className="mt-2 bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-11"
												 placeholder={ t('horse.custom_breed_placeholder') }/>
                )}
              </div>
              <div>
                <Label className="text-xs font-semibold text-[#666055] uppercase tracking-wider">
									{t('horse.gender')}
								</Label>
                <Select value={form.gender} onValueChange={v => update('gender', v)}>
                  <SelectTrigger data-testid="horse-gender"
																 className="mt-1 bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-11">
										<SelectValue />
									</SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mare">{t('horse.mare')}</SelectItem>
                    <SelectItem value="gelding">{t('horse.gelding')}</SelectItem>
                    <SelectItem value="stallion">{t('horse.stallion')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
							<div>
								<Label className="text-xs font-semibold text-[#666055] uppercase tracking-wider">
									{ t('horse.color') }
								</Label>
								<Select name="horse-color-select" value={ form.color } onValueChange={ handleColorChange }>
									<SelectTrigger data-testid="horse-color"
																 className="mt-1 bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-11">
										<SelectValue  placeholder={ t('horse.select_color') }/>
									</SelectTrigger>
									<SelectContent>
										{ coats.map(c =>
											<SelectItem key={ c.id } value={ c.name }>
												{ getTranslatedName(c, i18n.language) }
											</SelectItem>
										) }
										<SelectItem value={ OTHER }>{ t('horse.other') }</SelectItem>
									</SelectContent>
								</Select>
								{form.color === OTHER && (
									<Input data-testid="horse-custom-color" value={ customCoat }
												 onChange={ e => setCustomCoat(e.target.value) }
												 className="mt-2 bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-11"
												 placeholder={ t('horse.custom_coat_placeholder') }/>
								)}
              </div>
							<div>
								<Label className="text-xs font-semibold text-[#666055] uppercase tracking-wider">
									{ t('horse.date_of_birth') }
								</Label>
								<Input data-testid="horse-dob" type="date" value={ form.date_of_birth }
											 onChange={ e => update('date_of_birth', e.target.value) }
											 className="mt-1 bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-11"/>
							</div>
              <div>
                <Label className="text-xs font-semibold text-[#666055] uppercase tracking-wider">
									{t('horse.height')} (cm)
								</Label>
								<Input data-testid="horse-height" type="number" value={ form.height }
											 onChange={ e => update('height', e.target.value) }
											 className="mt-1 bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-11"/>
              </div>
              <div>
                <Label className="text-xs font-semibold text-[#666055] uppercase tracking-wider">
									{t('horse.weight')} ({t('horse.weight_unit')})
								</Label>
								<Input data-testid="horse-weight" type="number" value={ form.weight }
											 onChange={ e => update('weight', e.target.value) }
											 className="mt-1 bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-11"/>
              </div>
              <div>
                <Label className="text-xs font-semibold text-[#666055] uppercase tracking-wider">
									{t('horse.microchip_id')}
								</Label>
								<Input data-testid="horse-microchip" value={ form.microchip_id }
											 onChange={ e => update('microchip_id', e.target.value) }
											 className="mt-1 bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-11"
											 placeholder="e.g., 123456789012345"/>
              </div>
              <div className="flex items-center gap-6 pt-6">
                <div className="flex items-center gap-2">
                  <Switch data-testid="horse-travel-docs" checked={form.travel_documents}
													onCheckedChange={v => update('travel_documents', v)} />
                  <Label className="text-sm text-[#666055]">{t('horse.travel_docs')}</Label>
                </div>
              </div>
            </div>
            <div className="mt-4">
              <Label className="text-xs font-semibold text-[#666055] uppercase tracking-wider">
								{t('horse.health_issues')}
							</Label>
							<Textarea value={ form.health_issues } onChange={ e => update('health_issues', e.target.value) }
												className="mt-1 bg-[#F9F7F2] border-[#E6E2D6] rounded-sm"
												placeholder={ t('horse.health_placeholder') }/>
            </div>
            <div className="mt-4">
              <Label className="text-xs font-semibold text-[#666055] uppercase tracking-wider">
								{t('horse.stud_book')}
							</Label>
							<Input value={ form.stud_book_link } onChange={ e => update('stud_book_link', e.target.value) }
										 className="mt-1 bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-11" placeholder="https://..."/>
            </div>
          </Section>

          {/* Location */}
					<LocationEditor
						location_name={ form.location_name }
						location_lat={ form.location_lat }
						location_lng={ form.location_lng }
						update={ update }/>

          {/* Images */}
					<ImageUpload images={ images }
											 pricing={ pricing }
											 handleImages={ handleImages }
											 removeImage={ removeImage }
											 toggleImage={ toggleImage } />

          {/* Videos */}
					<VideoUpload videos={ videos }
											 pricing={ pricing }
											 handleVideos={ handleVideos }
											 removeVideo={ removeVideo }
											 toggleVideo={ toggleVideo } />

          {/* Featured Ad */}
					<FeatureAdToggle is_featured={ form.is_featured } pricing={ pricing }  update={ update } />

          {/* Cost Summary */}
          <CostSummary
						images={images}
						videos={videos}
						pricing={pricing}
						isFeatured={form.is_featured}
						featuredPaid={form.featured_paid}
						includedImages={form.included_images}
						includedVideos={form.included_videos}
						t={t} />

					<WaiverCheckBox
						images={ images }
						videos={ videos }
						pricing={ pricing }
						includedImages={ form.included_images }
						includedVideos={ form.included_videos }
						isFeatured={ form.is_featured }
						featuredPaid={ form.featured_paid }
						onClick={ setWaivedWithdrawalRight }
						state={ waivedWithdrawalRight }
						t={ t }/>

					<SubmitButton
						editId={ isClone ? null : editId }
						loading={ loading }
						label='horse.create_ad'
						images={ images }
						videos={ videos }
						pricing={ pricing }
						includedImages={ form.included_images }
						includedVideos={ form.included_videos }
						isFeatured={ form.is_featured }
						featuredPaid={ form.featured_paid }
						waivedWithdrawalRight={ waivedWithdrawalRight }
					/>

        </form>
      </div>
    </div>
  );
}